/**
 * ProCalculator Constants
 *
 * Centralized constants for the Professional Tariff Calculator
 */

/**
 * Default monthly consumption (kWh)
 * Represents average Polish household consumption
 */
export const DEFAULT_MONTHLY_CONSUMPTION = 260;

/**
 * Month names in Polish (full)
 */
export const MONTH_NAMES = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień',
] as const;

/**
 * Get current year dynamically
 * Used for default calculation year
 */
export function getDefaultCalculationYear(): number {
  return new Date().getFullYear();
}

/**
 * Default calculation year - always current year
 * @description Dynamic to avoid showing outdated year to users
 */
export const DEFAULT_CALCULATION_YEAR = getDefaultCalculationYear();

/**
 * Default distributor
 */
export const DEFAULT_DISTRIBUTOR = 'TAURON';

/**
 * Default handling fee type
 */
export const DEFAULT_HANDLING_FEE_TYPE = 'REGULATED';

/**
 * Default baseline tariff for comparison
 */
export const DEFAULT_BASELINE_TARIFF = 'G11';

/**
 * Year information for year selector
 * Labels clearly indicate which year is current vs archived
 */
export const YEAR_INFO: Record<number, string> = {
  2024: 'dane historyczne',
  2025: 'dane archiwalne (zamrożona cena do 30.06)',
  2026: 'aktualne taryfy URE',
};

/**
 * Get year info with fallback for unknown years
 */
export function getYearInfo(year: number): string {
  if (YEAR_INFO[year]) {
    return YEAR_INFO[year];
  }
  const currentYear = getDefaultCalculationYear();
  if (year > currentYear) {
    return 'przyszłe taryfy (gdy dostępne)';
  }
  return 'dane historyczne';
}

/**
 * Year change notifications
 */
export const YEAR_CHANGE_MESSAGES: Record<number, string> = {
  2024: 'Rok 2024 - dane historyczne.',
  2025: 'Rok 2025 - dane archiwalne z zamrożoną ceną energii do końca czerwca.',
  2026: 'Rok 2026 - aktualne taryfy URE. Wzrosły: opłata mocowa (+50%), OZE (+100%), kogeneracyjna (+45%).',
};

/**
 * Get year change message with fallback for unknown years
 */
export function getYearChangeMessage(year: number): string {
  if (YEAR_CHANGE_MESSAGES[year]) {
    return YEAR_CHANGE_MESSAGES[year];
  }
  return `Rok ${year} - dane historyczne.`;
}

/**
 * Tariff types that are always expected in backend response
 */
export const EXPECTED_TARIFF_TYPES = ['G11', 'G12', 'G12as', 'G12w', 'G13', 'G13s'] as const;
